import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, Stack, Chip, TextField, InputAdornment, Snackbar, Alert, Tooltip, IconButton, Menu, MenuItem, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useSelector } from 'react-redux';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import http from '../../Api/http';
import { GetEmployees } from '../../Api/Api';
import { card, solidBtn, ghostBtn, field, th, td, EmptyState } from '../uiKit';
import PermissionSettings from './PermissionSettings';
import { selectEffectiveModules, selectUser } from '../../redux/slices/authSlice';
import { selectActiveEntityId } from '../../redux/slices/orgSlice';
import { hasModule } from '../../data/serverModules';
import {
    readPermissions, addPermission, decidePermission, permissionStoreKey,
    permissionMinutes, fmtDuration, fmtTime12, fmtPermDate,
    // EMPLOYEE LIMIT CHECK (disabled for now - uncomment with the blocks marked below to turn it on)
    // readPolicy, resolveLimit, usedMinutes, fmtHours,
} from '../../utils/permissionStore';

const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const STATUS = {
    pending: { label: 'Pending', color: '#B45309', bg: '#FEF3C7' },
    approved: { label: 'Approved', color: '#16A34A', bg: '#DCFCE7' },
    rejected: { label: 'Rejected', color: '#DC2626', bg: '#FEE2E2' },
};
const StatusChip = ({ status }) => {
    const s = STATUS[status] || STATUS.pending;
    return <Chip label={s.label} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />;
};

// The three Permission screens live in one panel inside the Leave page. Each one is its own module, so a role
// sees only the screens it has been given under Roles & Access.
const VIEWS = [
    { key: 'apply', label: 'Apply Permission', module: 'permission-apply', icon: MoreTimeRoundedIcon },
    { key: 'approve', label: 'Approve Permission', module: 'permission-approve', icon: FactCheckRoundedIcon },
    { key: 'marked', label: 'Marked Permission', module: 'permission-marked', icon: BookmarkAddedRoundedIcon },
];

const REQUEST_COLS = ['EMPLOYEE', 'DATE', 'TIME (FROM – TO)', 'DURATION', 'REASON'];

function RequestCells({ p }) {
    return (
        <>
            <Box component="td" sx={{ ...td, px: 2.5 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{p.employeeName || '—'}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{[p.employeeCode, p.role].filter(Boolean).join(' · ')}</Typography>
            </Box>
            <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, color: '#334155', whiteSpace: 'nowrap' }}>{fmtPermDate(p.date)}</Box>
            <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, color: '#334155', whiteSpace: 'nowrap' }}>{fmtTime12(p.from)} – {fmtTime12(p.to)}</Box>
            <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>{fmtDuration(permissionMinutes(p.from, p.to))}</Box>
            <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, color: '#475569', maxWidth: 280 }}>{p.reason || '—'}</Box>
        </>
    );
}

function RequestTable({ rows, extraHeads = [], renderExtra, empty }) {
    if (!rows.length) return <EmptyState icon={MoreTimeRoundedIcon} title={empty.title} hint={empty.hint} />;
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
                <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                    <Box component="tr">
                        {[...REQUEST_COLS, ...extraHeads].map((h) => <Box component="th" key={h} sx={{ ...th, px: 2.5, ...(h === 'ACTIONS' && { textAlign: 'right' }) }}>{h}</Box>)}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.map((p, i) => (
                        <Box component="tr" key={p.id} sx={{ bgcolor: i % 2 ? '#FBFAFE' : '#fff', '&:hover': { bgcolor: '#F5F4FC' } }}>
                            <RequestCells p={p} />
                            {renderExtra(p)}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default function PermissionPanel() {
    const user = useSelector(selectUser);
    const entityId = useSelector(selectActiveEntityId);
    const modules = useSelector(selectEffectiveModules);
    const allowed = VIEWS.filter((v) => hasModule(modules, v.module));
    const [view, setView] = useState(allowed[0]?.key || 'apply');
    const current = allowed.find((v) => v.key === view) || allowed[0];

    const me = { code: String(user?.loginId || user?.employeeCode || ''), name: user?.name || user?.loginId || '', role: user?.role || '' };
    const canApprove = hasModule(modules, 'permission-approve');
    const [list, setList] = useState([]);
    const [snack, setSnack] = useState(null);
    // const [policy, setPolicy] = useState(() => readPolicy(entityId));
    const [settingsOpen, setSettingsOpen] = useState(false);
    // useEffect(() => { setPolicy(readPolicy(entityId)); }, [entityId]);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const reload = useCallback(() => setList(readPermissions(entityId)), [entityId]);
    useEffect(() => {
        reload();
        const onStorage = (e) => { if (e.key === permissionStoreKey(entityId)) reload(); };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [entityId, reload]);

    // ---- mark a permission for any employee (admin side, Marked Permission screen)
    const [employees, setEmployees] = useState([]);
    useEffect(() => {
        if (!canApprove) return undefined;
        let live = true;
        (async () => {
            try {
                const all = []; const seen = new Set();
                for (let page = 1; page <= 60; page += 1) {
                    const { data: b } = await http.get(GetEmployees, { params: { search: '', status: 'active', page, pageSize: 100 } });
                    const d = b?.data || {};
                    const items = Array.isArray(d.items) ? d.items : [];
                    const fresh = items.filter((e) => !seen.has(e.id ?? e.employeeCode));
                    fresh.forEach((e) => { seen.add(e.id ?? e.employeeCode); all.push(e); });
                    if (!fresh.length || all.length >= (Number(d.total) || 0)) break;
                }
                if (live) setEmployees(all.filter((e) => e.employeeCode).map((e) => ({ code: e.employeeCode, name: String(e.fullName || '').trim(), role: e.designation || '', department: e.department || '' })));
            } catch { if (live) setEmployees([]); }
        })();
        return () => { live = false; };
    }, [canApprove, entityId]);
    // ---- apply form
    const EMPTY = { date: todayIso(), from: '', to: '', reason: '' };
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const minutes = permissionMinutes(form.from, form.to);
    const timeBad = Boolean(form.from && form.to) && minutes === 0;
    // EMPLOYEE LIMIT CHECK (disabled for now). Hours HR allows this person against what they already took.
    // const myDept = user?.department || employees.find((e) => e.code === me.code)?.department || '';
    // const limit = resolveLimit(policy, me.code, myDept);
    // const used = usedMinutes(list, me.code, form.date);
    // const over = (cap, taken) => cap !== null && cap !== undefined && taken + minutes > cap * 60;
    // const overMonth = minutes > 0 && over(limit.month, used.month);
    // const overYear = minutes > 0 && over(limit.year, used.year);
    const save = () => {
        setTried(true);
        if (!form.date || !form.from || !form.to || minutes === 0 || !form.reason.trim()) return;
        // if (overMonth || overYear) return; // EMPLOYEE LIMIT CHECK
        addPermission(entityId, { employeeCode: me.code, employeeName: me.name, role: me.role, date: form.date, from: form.from, to: form.to, reason: form.reason.trim() });
        reload();
        setForm(EMPTY);
        setTried(false);
        notify('Permission request sent for approval.');
    };

    const MARK_EMPTY = { employee: null, date: todayIso(), from: '', to: '', reason: '' };
    const [mark, setMark] = useState(MARK_EMPTY);
    const [markTried, setMarkTried] = useState(false);
    const [markOpen, setMarkOpen] = useState(false);
    const markMinutes = permissionMinutes(mark.from, mark.to);
    const markTimeBad = Boolean(mark.from && mark.to) && markMinutes === 0;
    const saveMarked = () => {
        setMarkTried(true);
        if (!mark.employee || !mark.date || !mark.from || !mark.to || markMinutes === 0 || !mark.reason.trim()) return;
        addPermission(entityId, {
            employeeCode: mark.employee.code, employeeName: mark.employee.name, role: mark.employee.role,
            date: mark.date, from: mark.from, to: mark.to, reason: mark.reason.trim(), markedBy: me.name,
        }, true);
        reload();
        setMark(MARK_EMPTY);
        setMarkTried(false);
        setMarkOpen(false);
        notify(`Permission marked for ${mark.employee.name}.`);
    };

    const mine = useMemo(() => list.filter((p) => p.employeeCode === me.code), [list, me.code]);
    const pending = useMemo(() => list.filter((p) => p.status === 'pending'), [list]);
    // Marked = decided requests. Approvers see everyone's, everyone else sees their own.
    const marked = useMemo(() => list.filter((p) => p.status !== 'pending' && (canApprove || p.employeeCode === me.code)), [list, canApprove, me.code]);
    // Filters on the Marked Permission records.
    const [q, setQ] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [empF, setEmpF] = useState(null);
    const [rangeKind, setRangeKind] = useState('');
    const [rangeAnchor, setRangeAnchor] = useState(null);
    const markedEmployees = useMemo(() => {
        const m = new Map();
        marked.forEach((p) => { if (!m.has(p.employeeCode)) m.set(p.employeeCode, { code: p.employeeCode, name: p.employeeName }); });
        return [...m.values()].sort((x, y) => String(x.name).localeCompare(String(y.name)));
    }, [marked]);
    const markedShown = useMemo(() => {
        const t = q.trim().toLowerCase();
        return marked.filter((p) => (
            (!dateFrom || p.date >= dateFrom) && (!dateTo || p.date <= dateTo)
            && (!empF || p.employeeCode === empF.code)
            && (!t || [p.employeeName, p.employeeCode, p.reason].some((v) => String(v || '').toLowerCase().includes(t)))
        ));
    }, [marked, q, dateFrom, dateTo, empF]);
    const markedTotalMin = useMemo(() => markedShown.filter((p) => p.status === 'approved').reduce((n, p) => n + permissionMinutes(p.from, p.to), 0), [markedShown]);
    const filtersOn = Boolean(q || dateFrom || dateTo || empF || rangeKind);
    const clearFilters = () => { setQ(''); setDateFrom(''); setDateTo(''); setEmpF(null); setRangeKind(''); };
    const quickRange = (kind) => {
        setRangeKind(kind);
        setRangeAnchor(null);
        if (kind === 'custom') { setDateFrom(''); setDateTo(''); return; }
        const d = new Date();
        const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
        if (kind === 'today') { setDateFrom(todayIso()); setDateTo(todayIso()); }
        else if (kind === 'month') { setDateFrom(iso(new Date(d.getFullYear(), d.getMonth(), 1))); setDateTo(iso(new Date(d.getFullYear(), d.getMonth() + 1, 0))); }
        else { setDateFrom(`${d.getFullYear()}-01-01`); setDateTo(`${d.getFullYear()}-12-31`); }
    };

    const decide = (p, status) => {
        decidePermission(entityId, p.id, status, me.name);
        reload();
        notify(status === 'approved' ? 'Permission approved.' : 'Permission rejected.');
    };

    if (!allowed.length) {
        return <EmptyState icon={MoreTimeRoundedIcon} title="No access" hint="Ask an admin to give your role a Permission screen under Roles & Access." />;
    }

    return (
        <Box>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.6, p: 0.4, borderRadius: '7px', bgcolor: '#F3F4F6', border: '1px solid #E5E7EB', width: 'fit-content', flexWrap: 'wrap' }}>
                {allowed.map((v) => {
                    const on = current?.key === v.key;
                    return (
                        <Box key={v.key} onClick={() => setView(v.key)}
                            sx={{ px: 1.6, py: 0.7, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' : 'none' }}>
                            <v.icon sx={{ fontSize: 16, color: on ? PRIMARY : '#9CA3AF' }} />
                            <Typography sx={{ fontSize: '12.5px', fontWeight: on ? 700 : 600, color: on ? PRIMARY : '#6B7280' }}>{v.label}</Typography>
                            {v.key === 'approve' && pending.length > 0 && <Chip label={pending.length} size="small" sx={{ height: 18, fontSize: 10.5, fontWeight: 800, bgcolor: '#E11D48', color: '#fff' }} />}
                        </Box>
                    );
                })}
            </Box>
            {canApprove && (
                <Button startIcon={<TuneRoundedIcon />} onClick={() => setSettingsOpen(true)} sx={{ ...ghostBtn, height: 38, px: 2 }}>Permission Settings</Button>
            )}
            </Stack>

            {current?.key === 'apply' && (
                <Stack spacing={2.5}>
                    {/* ---- the form: one card, one tidy row of fields */}
                    <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 3, py: 2, borderBottom: '1px solid #EEF0F6' }}>
                            <MoreTimeRoundedIcon sx={{ color: PRIMARY, fontSize: 24 }} />
                            <Box>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A', lineHeight: 1.3 }}>Apply Permission</Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>Ask for a few hours off within a working day. Your request goes to an approver.</Typography>
                            </Box>
                        </Stack>
                        <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                                <TextField label="Date" required type="date" size="small" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                                    error={tried && !form.date} slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, gridColumn: { xs: '1 / -1', md: 'auto' } }} />
                                <TextField label="From" required type="time" size="small" value={form.from} onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
                                    error={tried && !form.from} slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                                <TextField label="To" required type="time" size="small" value={form.to} onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
                                    error={(tried && !form.to) || timeBad} helperText={timeBad ? 'Must be after From' : undefined} slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                                <TextField label="Duration" size="small" value={minutes ? fmtDuration(minutes) : '—'} disabled
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ ...field, '& .MuiOutlinedInput-root.Mui-disabled': { bgcolor: PRIMARY_LIGHT, '& input': { color: PRIMARY, WebkitTextFillColor: PRIMARY, fontWeight: 800 }, '& fieldset': { borderColor: '#E3DCFD' } }, gridColumn: { xs: '1 / -1', md: 'auto' } }} />
                                <TextField label="Reason" required size="small" fullWidth multiline minRows={2} value={form.reason}
                                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value.slice(0, 250) }))}
                                    error={tried && !form.reason.trim()} placeholder="Why do you need this permission?"
                                    helperText={`${form.reason.length}/250`} slotProps={{ formHelperText: { sx: { textAlign: 'right', mr: 0 } } }}
                                    sx={{ ...field, gridColumn: '1 / -1' }} />
                            </Box>
                            {/* EMPLOYEE LIMIT CHECK (disabled for now) - taken / allowed hours chips
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.8 }}>
                                {[['This month', limit.month, used.month, overMonth], ['This year', limit.year, used.year, overYear]].map(([label, cap, taken, bad]) => (
                                    <Chip key={label} size="small"
                                        label={cap === null || cap === undefined ? `${label}: ${fmtDuration(taken)} taken · no limit` : `${label}: ${fmtDuration(taken)} of ${fmtHours(cap)} taken`}
                                        sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: bad ? '#FEE2E2' : PRIMARY_LIGHT, color: bad ? '#DC2626' : PRIMARY }} />
                                ))}
                                {(overMonth || overYear) && <Typography sx={{ fontSize: 12, color: '#DC2626', fontWeight: 600 }}>This request goes over your {overMonth ? 'monthly' : 'yearly'} permission hours.</Typography>}
                            </Stack>
                            */}
                            <Stack direction="row" spacing={1.2} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                                <Button onClick={() => { setForm(EMPTY); setTried(false); }} sx={{ ...ghostBtn, height: 42, px: 3 }}>Clear</Button>
                                <Button onClick={save} /* disabled={overMonth || overYear} */ sx={{ ...solidBtn, height: 42, px: 5 }}>Save</Button>
                            </Stack>
                        </Box>
                    </Box>

                    {/* ---- my requests: a proper table */}
                    <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 3, py: 1.6, borderBottom: '1px solid #EEF0F6', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>My requests</Typography>
                            <Chip label={mine.length} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 11.5 }} />
                            <Stack direction="row" spacing={0.8} sx={{ ml: 'auto' }}>
                                {['pending', 'approved', 'rejected'].map((k) => (
                                    <Chip key={k} label={`${STATUS[k].label} ${mine.filter((p) => p.status === k).length}`} size="small" sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: STATUS[k].bg, color: STATUS[k].color }} />
                                ))}
                            </Stack>
                        </Stack>
                        {mine.length === 0 ? (
                            <EmptyState icon={MoreTimeRoundedIcon} title="No permission requests yet" hint="Fill in the form above and press Save — it shows up here." />
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                                    <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                                        <Box component="tr">
                                            {['DATE', 'TIME (FROM – TO)', 'DURATION', 'REASON', 'STATUS'].map((h) => <Box component="th" key={h} sx={{ ...th, px: 3 }}>{h}</Box>)}
                                        </Box>
                                    </Box>
                                    <Box component="tbody">
                                        {mine.map((p, i) => (
                                            <Box component="tr" key={p.id} sx={{ bgcolor: i % 2 ? '#FBFAFE' : '#fff', '&:hover': { bgcolor: '#F5F4FC' } }}>
                                                <Box component="td" sx={{ ...td, px: 3, fontSize: 13, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>{fmtPermDate(p.date)}</Box>
                                                <Box component="td" sx={{ ...td, px: 3, fontSize: 13, color: '#334155', whiteSpace: 'nowrap' }}>{fmtTime12(p.from)} – {fmtTime12(p.to)}</Box>
                                                <Box component="td" sx={{ ...td, px: 3, fontSize: 13, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>{fmtDuration(permissionMinutes(p.from, p.to))}</Box>
                                                <Box component="td" sx={{ ...td, px: 3, fontSize: 13, color: '#475569', maxWidth: 360 }}>{p.reason}</Box>
                                                <Box component="td" sx={{ ...td, px: 3, whiteSpace: 'nowrap' }}>
                                                    <StatusChip status={p.status} />
                                                    {p.status !== 'pending' && p.decidedBy && <Typography sx={{ fontSize: 11, color: '#98A0AE', mt: 0.3 }}>by {p.decidedBy}</Typography>}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Stack>
            )}

            {current?.key === 'approve' && (
                <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2.5, py: 1.6, borderBottom: '1px solid #EEF0F6' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Approve Permission</Typography>
                        <Chip label={`${pending.length} pending`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 11.5 }} />
                        <Typography sx={{ fontSize: 12, color: '#98A0AE', ml: 'auto' }}>Only roles given “Approve Permission” under Roles &amp; Access can see this screen.</Typography>
                    </Stack>
                    <RequestTable
                        rows={pending} extraHeads={['ACTIONS']}
                        renderExtra={(p) => (
                            <Box component="td" sx={{ ...td, px: 2.5, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {p.employeeCode === me.code ? (
                                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE', fontStyle: 'italic' }}>Your own request</Typography>
                                ) : (
                                    <>
                                        <Tooltip arrow title="Approve"><IconButton size="small" onClick={() => decide(p, 'approved')} sx={{ bgcolor: '#DCFCE7', borderRadius: '7px', mr: 0.8, '&:hover': { bgcolor: '#BBF7D0' } }}><CheckRoundedIcon sx={{ fontSize: 17, color: '#16A34A' }} /></IconButton></Tooltip>
                                        <Tooltip arrow title="Reject"><IconButton size="small" onClick={() => decide(p, 'rejected')} sx={{ bgcolor: '#FEE2E2', borderRadius: '7px', '&:hover': { bgcolor: '#FECACA' } }}><CloseRoundedIcon sx={{ fontSize: 17, color: '#DC2626' }} /></IconButton></Tooltip>
                                    </>
                                )}
                            </Box>
                        )}
                        empty={{ title: 'Nothing waiting for approval', hint: 'New permission requests appear here.' }}
                    />
                </Box>
            )}

            {current?.key === 'marked' && (
                <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2.5, py: 1.4, borderBottom: '1px solid #EEF0F6', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Marked Permission</Typography>
                        <Chip label={`${markedShown.length} record${markedShown.length === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 11.5 }} />
                        <Chip label={`Approved time: ${fmtDuration(markedTotalMin)}`} size="small" sx={{ bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 700, fontSize: 11.5 }} />
                        {canApprove && (
                            <Button startIcon={<AddRoundedIcon />} onClick={() => setMarkOpen(true)} sx={{ ...solidBtn, height: 38, px: 2, ml: 'auto' }}>Add Marked Permission</Button>
                        )}
                    </Stack>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 2.5, py: 1.4, borderBottom: '1px solid #EEF0F6', flexWrap: 'wrap', rowGap: 1.2 }}>
                        <TextField size="small" placeholder="Search name, ID, reason…" value={q} onChange={(e) => setQ(e.target.value)}
                            sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: 13, height: 40 } }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#9CA3AF' }} /></InputAdornment> } }} />
                        <Tooltip arrow title="Filter by date">
                            <IconButton onClick={(e) => setRangeAnchor(e.currentTarget)}
                                sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', height: 38, width: 38, color: rangeKind ? PRIMARY : '#64748B', bgcolor: rangeKind ? PRIMARY_LIGHT : '#fff' }}>
                                <CalendarMonthRoundedIcon sx={{ fontSize: 19 }} />
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={rangeAnchor} open={Boolean(rangeAnchor)} onClose={() => setRangeAnchor(null)}>
                            {[['today', 'Today'], ['month', 'This month'], ['year', 'This year'], ['custom', 'Custom']].map(([k, label]) => (
                                <MenuItem key={k} selected={rangeKind === k} onClick={() => quickRange(k)} sx={{ fontSize: 13.5, minWidth: 150 }}>{label}</MenuItem>
                            ))}
                        </Menu>
                        {rangeKind && rangeKind !== 'custom' && (
                            <Chip size="small" label={{ today: 'Today', month: 'This month', year: 'This year' }[rangeKind]} onDelete={() => { setRangeKind(''); setDateFrom(''); setDateTo(''); }}
                                sx={{ fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY }} />
                        )}
                        {rangeKind === 'custom' && (
                            <>
                                <TextField label="From" type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, width: 150 }} />
                                <TextField label="To" type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, width: 150 }} />
                            </>
                        )}
                        {canApprove && (
                            <Autocomplete size="small" options={markedEmployees} value={empF} onChange={(_, v) => setEmpF(v)} sx={{ width: 210 }}
                                getOptionLabel={(o) => o.name || o.code} isOptionEqualToValue={(a, b) => a.code === b.code}
                                renderInput={(params) => <TextField {...params} label="Employee" placeholder="All" sx={field} slotProps={{ ...params.slotProps, inputLabel: { shrink: true } }} />} />
                        )}
                        {filtersOn && <Button onClick={clearFilters} sx={{ ...ghostBtn, height: 34, px: 1.6 }}>Clear filters</Button>}
                    </Stack>
                    <RequestTable
                        rows={markedShown} extraHeads={['STATUS', 'MARKED BY']}
                        renderExtra={(p) => (
                            <>
                                <Box component="td" sx={{ ...td, px: 2.5 }}><StatusChip status={p.status} /></Box>
                                <Box component="td" sx={{ ...td, px: 2.5, fontSize: 12.5, color: '#64748B' }}>{p.decidedBy || '—'}</Box>
                            </>
                        )}
                        empty={{ title: filtersOn ? 'No records match the filters' : 'No marked permissions', hint: filtersOn ? 'Change or clear the filters.' : (canApprove ? 'Press "Add Marked Permission" to record one.' : 'Approved and rejected permissions show here.') }}
                    />
                </Box>
            )}

            <Dialog open={markOpen} onClose={() => setMarkOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Add Marked Permission</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>Record a permission for an employee directly. It is saved as approved.</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '14px !important' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        <Autocomplete
                            size="small" autoHighlight options={employees} value={mark.employee} sx={{ gridColumn: '1 / -1' }}
                            getOptionLabel={(o) => o.name || o.code}
                            isOptionEqualToValue={(a, b) => a.code === b.code}
                            onChange={(_, v) => setMark((m) => ({ ...m, employee: v }))}
                            noOptionsText="No employees found"
                            renderOption={(props, o) => (<li {...props} key={o.code}><Box><Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.name}</Typography><Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{[o.code, o.role].filter(Boolean).join(' · ')}</Typography></Box></li>)}
                            renderInput={(params) => <TextField {...params} label="Employee *" placeholder="Select employee" error={markTried && !mark.employee} sx={field} />}
                        />
                        <TextField label="Date *" type="date" size="small" value={mark.date} onChange={(e) => setMark((m) => ({ ...m, date: e.target.value }))}
                            error={markTried && !mark.date} slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, gridColumn: { xs: '1 / -1', sm: 'auto' } }} />
                        <TextField label="From *" type="time" size="small" value={mark.from} onChange={(e) => setMark((m) => ({ ...m, from: e.target.value }))}
                            error={markTried && !mark.from} slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                        <TextField label="To *" type="time" size="small" value={mark.to} onChange={(e) => setMark((m) => ({ ...m, to: e.target.value }))}
                            error={(markTried && !mark.to) || markTimeBad} helperText={markTimeBad ? 'To must be after From.' : (markMinutes ? `Duration: ${fmtDuration(markMinutes)}` : undefined)}
                            slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                        <TextField label="Reason *" size="small" multiline minRows={2} value={mark.reason} onChange={(e) => setMark((m) => ({ ...m, reason: e.target.value }))}
                            error={markTried && !mark.reason.trim()} sx={{ ...field, gridColumn: '1 / -1' }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.8 }}>
                    <Button onClick={() => { setMark(MARK_EMPTY); setMarkTried(false); setMarkOpen(false); }} sx={{ ...ghostBtn, height: 40, px: 3 }}>Cancel</Button>
                    <Button onClick={saveMarked} sx={{ ...solidBtn, height: 40, px: 4 }}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Approvers only: the dialog loads the departments list, which employees may not call (the server
                answers MODULE_ACCESS_DENIED and the app bounces to the dashboard). */}
            {canApprove && settingsOpen && (
                <PermissionSettings
                    open={settingsOpen} onClose={() => setSettingsOpen(false)} entityId={entityId}
                    onSaved={() => { /* setPolicy(p); */ notify('Permission settings saved.'); }} />
            )}

            <Snackbar open={Boolean(snack)} autoHideDuration={3200} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
